(()=>{'use strict';
/** Same-origin embedded game document; storefront response headers prohibit URL framing. */
const isThemeGameSource = (source, themeId) => {
  try {
    const url = new URL(source, document.baseURI);
    const expected = new URL(window.PinballFXV15.gameUrl, document.baseURI);
    return ['file:', 'http:', 'https:'].includes(window.location.protocol) &&
      themeId === String(window.PinballFXV15.themeId) &&
      url.origin === window.location.origin && url.href === expected.href &&
      !url.username && !url.password && !url.hash;
  } catch (_) { return false; }
};
function connectPinball(iframe, { gameOrigin, themeId = iframe.dataset.reviewThemeId, onState = () => {} }) {
  const localFile = window.location.protocol === 'file:';
  const origin = localFile ? 'null' : window.location.origin;
  if (!localFile && (origin === 'null' || origin !== new URL(gameOrigin).origin)) throw new Error('Unexpected local pinball game origin');
  const token = iframe.dataset.reviewChannel ||= Array.from(crypto.getRandomValues(new Uint8Array(24)),b=>b.toString(16).padStart(2,'0')).join('');
  const targetOrigin = localFile ? '*' : origin;
  const abort = new AbortController(), held = new Set();
  let disposed = false, accepting = false, connected = false, retry = 0, heartbeat = 0, attempts = 0, visible = false;
  const loaded = () => iframe.getAttribute('src') !== 'about:blank' && isThemeGameSource(iframe.dataset.gameDocumentSource, themeId);
  const send = payload => { if (!disposed && loaded()) iframe.contentWindow?.postMessage({ channel: 'xarcade-pinball', version: 1, token, ...payload }, targetOrigin); };
  const stopHeartbeat = () => { clearInterval(heartbeat); heartbeat = 0; };
  const releaseAll = () => { held.clear(); stopHeartbeat(); send({ type: 'release-all' }); };
  const control = (action, pressed) => {
    if (!['flipL', 'flipR', 'flipU'].includes(action) || (pressed && !accepting)) return;
    if (pressed) held.add(action); else held.delete(action);
    send({ type: 'control', action, pressed: !!pressed });
    if (held.size && !heartbeat) heartbeat = setInterval(() => { if (document.hidden) releaseAll(); else send({ type: 'heartbeat' }); }, 500);
    else if (!held.size) stopHeartbeat();
  };
  const sendVisibility = () => send({ type: 'host-visibility', visible: visible && !document.hidden });
  const setVisible = value => { visible = value === true; if (!visible || document.hidden) releaseAll(); sendVisibility(); };
  const connect = () => { send({ type: 'connect' }); sendVisibility(); };
  const start = () => {
    clearInterval(retry); accepting = connected = false; attempts = 0; releaseAll();
    if (!loaded()) return;
    connect();
    retry = setInterval(() => { if (connected || document.hidden || ++attempts > 30) { clearInterval(retry); retry = 0; return; } connect(); }, 1000);
  };
  window.addEventListener('message', event => {
    if (!loaded() || event.source !== iframe.contentWindow || event.origin !== origin) return;
    const data = event.data;
    if (!data || data.token !== token || data.channel !== 'xarcade-pinball' || data.version !== 1 || data.type !== 'state') return;
    const wasAccepting = accepting;
    accepting = data.acceptingInput === true; connected = true; clearInterval(retry); retry = 0;
    if (wasAccepting && !accepting) releaseAll();
    onState(data);
  }, { signal: abort.signal });
  iframe.addEventListener('load', () => {
    if (loaded()) {
      try {
        const gameWindow = iframe.contentWindow;
        gameWindow.addEventListener('pointerdown', () => gameWindow.focus(), { capture: true, signal: abort.signal });
      } catch (_) {}
    }
    start();
  }, { signal: abort.signal });
  const observer = new MutationObserver(start); observer.observe(iframe, { attributes: true, attributeFilter: ['src', 'srcdoc'] });
  window.addEventListener('blur', releaseAll, { signal: abort.signal });
  window.addEventListener('pagehide', releaseAll, { signal: abort.signal });
  document.addEventListener('visibilitychange', () => { if (document.hidden) { releaseAll(); sendVisibility(); } else if (loaded()) connect(); }, { signal: abort.signal });
  start();
  return { control, releaseAll, connect, setVisible, get acceptingInput() { return accepting; }, dispose() { setVisible(false); releaseAll(); disposed = true; clearInterval(retry); abort.abort(); observer.disconnect(); } };
}

// Explicit travel states are separate from hardware specifications and game readiness.
function createStageInput({ paint, send, schedule = setTimeout, cancel = clearTimeout }) {
  let travel = 0, source = '', timer = 0, disposed = false;
  function apply(value) { if (disposed) return; travel = Math.max(0, Math.min(100, Number(value) || 0)); paint(travel); send(travel >= 50, travel >= 98); }
  function release() { cancel(timer); timer = 0; source = ''; apply(0); }
  return {
    get travel() { return travel; }, get active() { return source !== ''; },
    down() { if (source === 'pad' || disposed) return; cancel(timer); source = 'pad'; apply(50); timer = schedule(() => { timer = 0; if (source === 'pad') apply(100); }, 500); },
    slider(value) { cancel(timer); timer = 0; source = Number(value) ? 'slider' : ''; apply(value); },
    release,
    dispose() { release(); disposed = true; }
  };
}

const instances = new Map();
function init(scope) {
  const roots = [...scope.querySelectorAll('[data-zx-token-stage]')];
  if (scope.matches?.('[data-zx-token-stage]')) roots.unshift(scope);
  roots.forEach(root => {
    if (instances.has(root)) return;
    const iframe = root.querySelector('[data-zx-token-frame]'), pad = root.querySelector('[data-zx-stage-pad]'), range = root.querySelector('[data-zx-stage-range]'), status = root.querySelector('[data-zx-token-status]');
    if (!iframe || !pad || !range) return;
    const life = window.ZXReview.lifecycle(root);
    const mobileView = window.matchMedia('(max-width: 960px)');
    let api, input, pointer = null;
    const paint = value => {
      const lower = value >= 50, upper = value >= 98;
      root.style.setProperty('--travel', value / 100);
      root.classList.toggle('is-stage1', lower && !upper); root.classList.toggle('is-stage2', upper);
      root.querySelectorAll('[data-zx-stage-lower],[data-zx-stage-mark="1"],[data-zx-stage-chip="1"]').forEach(el => el.classList.toggle('is-on', lower));
      root.querySelectorAll('[data-zx-stage-upper],[data-zx-stage-mark="2"],[data-zx-stage-chip="2"]').forEach(el => el.classList.toggle('is-on', upper));
      const readout = root.querySelector('[data-zx-stage-readout]'), volt = root.querySelector('[data-zx-stage-volt]');
      if (readout) readout.textContent = Math.round(value) + '%';
      if (volt) volt.textContent = 'Illustration';
      range.value = value; range.setAttribute('aria-valuetext', upper ? 'Both stages engaged' : lower ? 'Lower stage engaged' : 'Released');
      pad.setAttribute('aria-pressed', String(lower));
    };
    api = connectPinball(iframe, { gameOrigin: window.location.origin, themeId: iframe.dataset.reviewThemeId, onState: state => {
      const visibility = state.hostVisible === true ? 'visible' : 'suspended';
      iframe.setAttribute('data-game-host-visibility', visibility);
      root.setAttribute('data-game-host-visibility', visibility);
      if (status) status.textContent = state.acceptingInput ? 'Token Storm browser demo connected · button and slider control the right lower flipper and independent upper stage.' : !state.ready ? 'The Token Storm browser demo is loading · the button demonstration remains available.' : state.hostVisible === false ? 'Demo paused offscreen · your game is preserved.' : 'The Token Storm browser demo is paused · resume it to connect the controls.';
      if (input && !input.active && state.acceptingInput && state.controls) paint(state.controls.flipU ? 100 : state.controls.flipL || state.controls.flipR ? 50 : 0);
    } });
    input = createStageInput({ paint, send: (lower, upper) => { api.control('flipR', lower); api.control('flipU', upper); } });
    const release = () => { input.release(); api.releaseAll(); if (pointer !== null) { const previous = pointer; pointer = null; try { pad.releasePointerCapture(previous); } catch (_) {} } };
    pad.disabled = range.disabled = false; paint(0);
    if (status) status.textContent = 'Token Storm is a separate X-Arcade browser demo · timed holds illustrate deeper travel.';
    life.on(pad, 'pointerdown', event => { if (event.button > 0 || pointer !== null) return; event.preventDefault(); pointer = event.pointerId; pad.setPointerCapture?.(pointer); api.connect(); input.down(); });
    ['pointerup', 'pointercancel', 'lostpointercapture', 'blur'].forEach(type => life.on(pad, type, release));
    life.on(pad, 'keydown', event => { if (['Space', 'Enter'].includes(event.code)) { event.preventDefault(); if (!event.repeat) { api.connect(); input.down(); } } });
    life.on(pad, 'keyup', event => { if (['Space', 'Enter'].includes(event.code)) { event.preventDefault(); release(); } });
    life.on(range, 'input', () => input.slider(range.value)); life.on(range, 'blur', release); life.on(range, 'pointercancel', release);
    life.on(window, 'blur', release); life.on(window, 'pagehide', release); life.on(document, 'visibilitychange', () => { if (document.hidden) release(); });
    life.observe('IntersectionObserver', entries => { const visible = entries[0].isIntersecting && !mobileView.matches; api.setVisible(visible); if (!visible) release(); }, { threshold: 0.01 }).observe(iframe);
    // Load once near the viewport; the separate visibility observer controls rendering after load.
    const gameDocument = root.querySelector('template[data-fx-token-document]');
    let gameObjectUrl = '';
    let gameRequested = iframe.getAttribute('src') !== 'about:blank' && isThemeGameSource(iframe.dataset.gameDocumentSource, iframe.dataset.reviewThemeId), nearViewport = false;
    const loadGame = () => {
      if (mobileView.matches || gameRequested || life.disposed || document.hidden) return;
      const source = iframe.dataset.reviewGameSrc;
      if (!isThemeGameSource(source, iframe.dataset.reviewThemeId) || !gameDocument || gameDocument.dataset.reviewThemeId !== iframe.dataset.reviewThemeId || !gameDocument.content.querySelector('#game')) return;
      gameRequested = true;
      iframe.dataset.gameDocumentSource = new URL(source, document.baseURI).href;
      const documentCopy = document.createElement('template');
      documentCopy.content.append(gameDocument.content.cloneNode(true));
      documentCopy.content.querySelectorAll('[src], [href]').forEach(element => {
        ['src', 'href'].forEach(attribute => {
          const value = element.getAttribute(attribute);
          if (!value || /^(?:#|data:|blob:)/i.test(value)) return;
          element.setAttribute(attribute, new URL(value, iframe.dataset.gameDocumentSource).href);
        });
      });
      const gameHtml = '<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><link rel="icon" href="data:,"><title>Token Storm</title></head><body>' + documentCopy.innerHTML + '</body></html>';
      gameObjectUrl = URL.createObjectURL(new Blob([gameHtml], { type: 'text/html;charset=utf-8' }));
      iframe.src = gameObjectUrl + '#review-channel=' + iframe.dataset.reviewChannel;
      if (status) status.textContent = 'The Token Storm browser demo is loading · the button demonstration remains available.';
    };
    life.observe('IntersectionObserver', entries => {
      nearViewport = entries[0].isIntersecting;
      if (nearViewport) loadGame();
    }, { rootMargin: '200px 0px', threshold: 0 }).observe(iframe);
    life.on(mobileView, 'change', () => {
      if (!mobileView.matches) {
        if (nearViewport) loadGame();
        return;
      }
      release();
      api.setVisible(false);
      iframe.src = 'about:blank';
      delete iframe.dataset.gameDocumentSource;
      iframe.removeAttribute('data-game-host-visibility');
      root.removeAttribute('data-game-host-visibility');
      if (gameObjectUrl) URL.revokeObjectURL(gameObjectUrl);
      gameObjectUrl = '';
      gameRequested = false;
    });
    life.on(document, 'visibilitychange', () => { if (nearViewport && !document.hidden) loadGame(); });
    instances.set(root, life);
    life.cleanup(() => { input.dispose(); api.dispose(); if (gameObjectUrl) URL.revokeObjectURL(gameObjectUrl); instances.delete(root); });
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document)); else init(document);
document.addEventListener('shopify:section:load', event => init(event.target));

})();


